import { NAME_MAX_LENGTH, PASSWORD_MAX_LENGTH } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { Button, Input, Label } from "@sagot/ui";
import type { SyntheticEvent } from "react";
import { FormAlert } from "./components/form-alert";
import { PasswordInput } from "./components/password-input";
import { PasswordMeter } from "./components/password-strength";
import { isNameOk, isPasswordOk, passwordHelp, registerPasswordScore, type PasswordHelp } from "./register-rules";
import { PaneTitle, RegisterNav, RegisterPane } from "./register-pane";
import { useFieldSeen } from "./use-field-seen";

const HELP_ID = "r-pw-help";
const NAME_ERROR_ID = "r-name-error";
const STRENGTH_KEYS = { 1: "auth.register.pwWeak", 2: "auth.register.pwOkay", 3: "auth.register.pwGood", 4: "auth.register.pwStrong" } as const;

function usePasswordHelpText(): (help: PasswordHelp) => string {
  const { t } = useTranslation();
  return (help) => {
    switch (help.kind) {
      case "empty":
        return t("auth.password.hintEmpty");
      case "short":
        return help.missing === 1 ? t("auth.register.pwMoreOne") : t("auth.register.pwMoreMany", { count: help.missing });
      case "mix":
        return t("auth.register.pwMix");
      case "score":
        return t(STRENGTH_KEYS[help.score]);
    }
  };
}

interface RegisterAboutStepProps {
  name: string;
  password: string;
  onChange: (patch: { name?: string; password?: string }) => void;
  /** The API refused the password (too weak or found in a breach): the message, shown above the form. */
  error: string | null;
  onDismissError: () => void;
  /** The visitor refreshed the page: their answers are back, the password is not. */
  resumed: boolean;
  back: boolean;
  onBack: () => void;
  onNext: () => void;
}

/** Step 2 (mockup pane 3): full name, password with the strength bar. Continue waits for both to be good enough. */
export function RegisterAboutStep({ name, password, onChange, error, onDismissError, resumed, back, onBack, onNext }: RegisterAboutStepProps) {
  const { t } = useTranslation();
  const helpText = usePasswordHelpText();
  const help = passwordHelp(password);
  const ready = isNameOk(name) && isPasswordOk(password);
  const mixMissing = help.kind === "mix";
  // Continue is off while the name is missing; the field says why once the visitor has left it (or has a good password and no name).
  const nameField = useFieldSeen();
  const nameBad = !isNameOk(name) && (nameField.seen || isPasswordOk(password));

  const submit = (event: SyntheticEvent) => {
    event.preventDefault();
    if (ready) onNext();
  };

  return (
    <RegisterPane back={back} onSubmit={submit}>
      <PaneTitle>{t("auth.register.aboutYou")}</PaneTitle>
      <p className="a-sub">{t("auth.register.aboutSub")}</p>
      {error ? <FormAlert title={error} onDismiss={onDismissError} /> : null}
      {resumed && !error ? <FormAlert tone="info" title={t("auth.register.resumeNote")} /> : null}
      <div className="a-fld">
        <Label htmlFor="r-name">{t("auth.register.fullName")}</Label>
        <Input
          id="r-name"
          autoComplete="name"
          placeholder={t("auth.register.namePlaceholder")}
          maxLength={NAME_MAX_LENGTH}
          value={name}
          invalid={nameBad || undefined}
          aria-describedby={nameBad ? NAME_ERROR_ID : undefined}
          onChange={(event) => {
            onChange({ name: event.target.value });
          }}
          onBlur={nameField.onBlur}
          onKeyDown={(event) => {
            if (event.key === "Enter") nameField.markSeen();
          }}
        />
        {nameBad ? (
          <p id={NAME_ERROR_ID} className="hint hint-err" role="alert">
            {t("auth.common.nameRequired")}
          </p>
        ) : null}
      </div>
      <div className="a-fld">
        <Label htmlFor="r-password">{t("auth.common.password")}</Label>
        <PasswordInput
          id="r-password"
          autoComplete="new-password"
          placeholder={t("auth.password.placeholder")}
          maxLength={PASSWORD_MAX_LENGTH}
          value={password}
          aria-describedby={HELP_ID}
          aria-invalid={mixMissing || error ? true : undefined}
          onChange={(event) => {
            onChange({ password: event.target.value });
            if (error) onDismissError();
          }}
        />
        <PasswordMeter score={registerPasswordScore(password)} help={helpText(help)} invalid={mixMissing} helpId={HELP_ID} />
      </div>
      <RegisterNav backLabel={t("auth.register.back")} onBack={onBack}>
        <Button type="submit" size="lg" disabled={!ready}>
          {t("auth.register.continue")}
        </Button>
      </RegisterNav>
    </RegisterPane>
  );
}
